/**
 * Footer Widerruf modal — open/close shell + Gorgias Contact Form bridge.
 * Extra HTML: docs/gorgias-widerruf-extra-html.html (UID lch1m0t5)
 *
 * Also answers WIDERRUF_LOCALE_REQUEST from the Gorgias iframe (Extra HTML)
 * with this storefront page's locale, since the iframe is cross-origin and
 * cannot reliably read it via document.referrer.
 */
class VelyFooterSupportModal extends HTMLElement {
  connectedCallback() {
    if (this._isReady) return;
    this._isReady = true;

    this.dialog = this.querySelector('.vely-footer-support-modal__dialog');
    this.formSlot = this.querySelector('[data-vely-widerruf-gorgias-form-slot]');
    this.activeTrigger = null;
    this._gorgiasBridgeReady = false;
    this._iframeObserver = null;
    this._savedScrollX = 0;
    this._savedScrollY = 0;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onGorgiasMessage = this.onGorgiasMessage.bind(this);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    this.initGorgiasBridge();
    this.observeGorgiasIframe();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('message', this.onGorgiasMessage);
    this.stopIframeObserver();
    this.closeDialog();
    this._isReady = false;
    this._gorgiasBridgeReady = false;
  }

  static get GORGIAS_ORIGIN() {
    return 'https://contact.gorgias.help';
  }

  static get GORGIAS_MESSAGE_SOURCE() {
    return 'ultimate-setup-widerruf-gorgias';
  }

  /* Must match the locale keys defined in the Gorgias Widerruf Extra HTML's COPY object. */
  static get SUPPORTED_LOCALES() {
    return ['de', 'en', 'fr'];
  }

  static get FORM_RESET_ATTR() {
    return 'widerrufNeedsFormReset';
  }

  static get ORIGINAL_SRC_ATTR() {
    return 'widerrufOriginalSrc';
  }

  getFooterRoot() {
    return this.closest('.vely-footer');
  }

  capturePageScroll() {
    this._savedScrollX = window.scrollX || window.pageXOffset || 0;
    this._savedScrollY = window.scrollY || window.pageYOffset || 0;
  }

  restorePageScroll() {
    const x = this._savedScrollX || 0;
    const y = this._savedScrollY || 0;
    window.scrollTo(x, y);
    window.requestAnimationFrame(() => {
      window.scrollTo(x, y);
    });
  }

  getGorgiasIframe() {
    if (!this.formSlot) return null;
    return (
      this.formSlot.querySelector('iframe[data-gorgias-iframe-contact-form]') ||
      this.formSlot.querySelector('iframe')
    );
  }

  normalizeGorgiasEmbedSrc(src) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.href);
      url.searchParams.delete('_widerruf_reload');
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  preserveGorgiasIframeSrc(iframe) {
    if (!iframe) return '';

    const currentSrc = this.normalizeGorgiasEmbedSrc(
      (iframe.getAttribute('src') || iframe.src || '').trim()
    );

    if (currentSrc && currentSrc !== 'about:blank') {
      if (!iframe.dataset[VelyFooterSupportModal.ORIGINAL_SRC_ATTR]) {
        iframe.dataset[VelyFooterSupportModal.ORIGINAL_SRC_ATTR] = currentSrc;
      }
    }

    return iframe.dataset[VelyFooterSupportModal.ORIGINAL_SRC_ATTR] || '';
  }

  reloadGorgiasIframe() {
    const iframe = this.getGorgiasIframe();
    if (!iframe) return false;

    const originalSrc = this.preserveGorgiasIframeSrc(iframe);
    if (!originalSrc) return false;

    try {
      const url = new URL(originalSrc, window.location.href);
      url.searchParams.set('_widerruf_reload', String(Date.now()));
      const reloadSrc = url.toString();
      iframe.setAttribute('src', reloadSrc);
      iframe.src = reloadSrc;
    } catch (error) {
      iframe.setAttribute('src', originalSrc);
      iframe.src = originalSrc;
    }

    return true;
  }

  markGorgiasFormForReset() {
    this.preserveGorgiasIframeSrc(this.getGorgiasIframe());
    this.dataset[VelyFooterSupportModal.FORM_RESET_ATTR] = '1';
  }

  maybeResetGorgiasFormOnOpen() {
    if (this.dataset[VelyFooterSupportModal.FORM_RESET_ATTR] !== '1') return false;
    if (!this.reloadGorgiasIframe()) return false;

    delete this.dataset[VelyFooterSupportModal.FORM_RESET_ATTR];
    this.classList.remove('vely-footer-support-modal--success');
    return true;
  }

  /*
   * The Widerruf Gorgias form renders inside a cross-origin iframe
   * (contact.gorgias.help), so its script cannot read window.location of
   * this storefront page — and document.referrer is unreliable there
   * (origin-only under the browser's default strict-origin-when-cross-origin
   * policy). Read the real page locale here and answer the iframe's
   * request with it over postMessage.
   */
  detectPageLocale() {
    try {
      const segments = (window.location.pathname || '/').split('/').filter(Boolean);
      if (!segments.length) return 'de';
      const first = String(segments[0] || '').toLowerCase();
      if (VelyFooterSupportModal.SUPPORTED_LOCALES.includes(first)) return first;
      const short = first.split('-')[0];
      if (VelyFooterSupportModal.SUPPORTED_LOCALES.includes(short)) return short;
    } catch (e) {
      /* ignore */
    }
    return 'de';
  }

  postLocale(targetWindow) {
    if (!targetWindow) return;
    try {
      targetWindow.postMessage(
        { source: VelyFooterSupportModal.GORGIAS_MESSAGE_SOURCE, type: 'WIDERRUF_LOCALE', locale: this.detectPageLocale() },
        '*'
      );
    } catch (e) {
      /* Ignore cross-origin delivery failures. */
    }
  }

  isMessageFromModalIframe(event) {
    const iframe = this.getGorgiasIframe();
    if (!iframe || !iframe.contentWindow) return false;
    if (event.source === iframe.contentWindow) return true;
    /* Nested Gorgias subframe inside the modal embed — origin is already strict. */
    return true;
  }

  updateGorgiasLoadedState() {
    const hasIframe = Boolean(this.getGorgiasIframe());
    this.classList.toggle('is-gorgias-loaded', hasIframe);
  }

  observeGorgiasIframe() {
    if (!this.formSlot || typeof MutationObserver === 'undefined') {
      this.updateGorgiasLoadedState();
      return;
    }

    this.updateGorgiasLoadedState();

    this._iframeObserver = new MutationObserver(() => {
      this.updateGorgiasLoadedState();
    });

    this._iframeObserver.observe(this.formSlot, {
      childList: true,
      subtree: true
    });
  }

  stopIframeObserver() {
    if (this._iframeObserver) {
      this._iframeObserver.disconnect();
      this._iframeObserver = null;
    }
  }

  enterModalSuccessMode() {
    if (!this.classList.contains('is-open')) return;

    /* Keep scroll lock — do NOT remove vely-footer-support-modal-open. */
    this.classList.add('vely-footer-support-modal--success');

    /*
     * Resize can change dialog geometry; restore parent scroll so the
     * underlying Shopify page does not jump during success layout.
     * Do NOT focus success UI — that causes parent scroll jumps.
     */
    this.restorePageScroll();
    window.requestAnimationFrame(() => {
      this.restorePageScroll();
      window.dispatchEvent(new Event('resize'));
      this.restorePageScroll();
    });
    window.setTimeout(() => this.restorePageScroll(), 0);
    window.setTimeout(() => this.restorePageScroll(), 100);
  }

  initGorgiasBridge() {
    if (this._gorgiasBridgeReady) return;
    window.addEventListener('message', this.onGorgiasMessage);
    this._gorgiasBridgeReady = true;
  }

  onGorgiasMessage(event) {
    if (event.origin !== VelyFooterSupportModal.GORGIAS_ORIGIN) return;

    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.source !== VelyFooterSupportModal.GORGIAS_MESSAGE_SOURCE) return;
    if (!this.isConnected) return;

    /* Answer directly to the requesting window — no iframe lookup needed. */
    if (data.type === 'WIDERRUF_LOCALE_REQUEST') {
      this.postLocale(event.source);
      return;
    }

    if (!this.isMessageFromModalIframe(event)) return;

    if (data.type === 'WIDERRUF_GORGIAS_SUBMITTING') {
      /* Capture scroll right before Gorgias submission; do not change modal state. */
      this.capturePageScroll();
      return;
    }

    if (data.type === 'WIDERRUF_GORGIAS_SUCCESS') {
      this.enterModalSuccessMode();
      return;
    }

    if (data.type === 'WIDERRUF_GORGIAS_CLOSE') {
      this.closeDialog({ resetForm: true });
      return;
    }

    if (data.type === 'WIDERRUF_GORGIAS_CANCEL') {
      this.closeDialog({ resetForm: false });
    }
  }

  onDocumentClick(event) {
    const footer = this.getFooterRoot();
    if (!footer) return;

    const openTrigger = event.target.closest('[data-vely-footer-support-open]');
    if (openTrigger && footer.contains(openTrigger)) {
      event.preventDefault();
      this.openDialog(openTrigger);
      return;
    }

    if (!this.classList.contains('is-open')) return;

    const closeTrigger = event.target.closest('[data-vely-footer-support-close]');
    if (closeTrigger && this.contains(closeTrigger)) {
      event.preventDefault();
      this.closeDialog({ resetForm: false });
      return;
    }

    if (event.target.classList.contains('vely-footer-support-modal__backdrop') && this.contains(event.target)) {
      event.preventDefault();
      this.closeDialog({ resetForm: false });
    }
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog({ resetForm: false });
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  openDialog(trigger) {
    this.activeTrigger = trigger;
    this.capturePageScroll();

    this.maybeResetGorgiasFormOnOpen();
    this.preserveGorgiasIframeSrc(this.getGorgiasIframe());

    this.classList.remove('vely-footer-support-modal--success');
    this.classList.add('is-open');
    document.documentElement.classList.add('vely-footer-support-modal-open');
    this.dialog?.setAttribute('aria-hidden', 'false');

    const focusTarget = this.dialog?.querySelector('.vely-footer-support-modal__close');
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (error) {
        focusTarget.focus();
        this.restorePageScroll();
      }
    }

    window.requestAnimationFrame(() => {
      this.restorePageScroll();
      window.dispatchEvent(new Event('resize'));
      this.restorePageScroll();
    });
  }

  closeDialog({ resetForm = false } = {}) {
    const active = document.activeElement;
    if (this.dialog && active && this.dialog.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }

    this.classList.remove('is-open', 'vely-footer-support-modal--success');
    document.documentElement.classList.remove('vely-footer-support-modal-open');
    this.dialog?.setAttribute('aria-hidden', 'true');

    if (resetForm) {
      this.markGorgiasFormForReset();
    }

    /* Restore scroll BEFORE focus so trigger.focus() cannot jump the page. */
    this.restorePageScroll();

    if (this.activeTrigger && document.contains(this.activeTrigger)) {
      try {
        this.activeTrigger.focus({ preventScroll: true });
      } catch (error) {
        this.activeTrigger.focus();
        this.restorePageScroll();
      }
    }

    window.requestAnimationFrame(() => {
      this.restorePageScroll();
    });
  }

  trapFocus(event) {
    if (!this.dialog) return;

    const focusableElements = this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('vely-footer-support-modal')) {
  customElements.define('vely-footer-support-modal', VelyFooterSupportModal);
}
